// @ts-strict-ignore
import { createPredicatedProcessor } from '../../lib/predicatedProcessor';
import type {
  ActionAddedChange,
  ActionModifiedChange,
  ActionRemovedChange,
  ActionRuleGroupingModifiedChange,
  Change,
  ChangelogInputBundle,
  ThresholdAddedChange,
  ThresholdModifiedChange,
  ThresholdRemovedChange,
} from '../../types';
import { Operation } from '../../types';
import type { MergeBuffer, MergeBufferItem } from './types';


export function createChangeProcessors(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  entriesBuffer: MergeBuffer;
  formattedOperation: Operation;
}) {
  const { entriesBuffer, formattedOperation } = args;

  const createChangeProcessor = createPredicatedProcessor<Change>;

  // note: snippet actions should not be included in the diff result
  return {
    actionAddedOrModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor, op }) => (descriptor === 'action') && (op === Operation.Add),
        ({ descriptor, op }) => (descriptor === 'action') && (op === Operation.Modify),
      ],
      process: (
        addedActionChanges?: ActionAddedChange[],
        modifiedActionChanges?: ActionModifiedChange[],
      ) => {
        const entries: MergeBufferItem[] = [
          ...(addedActionChanges ?? []),
          ...(modifiedActionChanges ?? []),
        ].flatMap(change => change.actionPublicId);

        entriesBuffer.append(entries);
      },
    }),
    rulesModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => (descriptor === 'action.rules.grouping') && (op === Operation.Modify),
      ],
      process: (
        rulesModifiedChanges?: ActionRuleGroupingModifiedChange[],
      ) => {
        const entries: MergeBufferItem[] = rulesModifiedChanges.flatMap(change => {
          const {
            actionPublicId,
            value: { prev: previousRuleIdSet, next: nextRuleIdSet },
          } = change;

          // We can consider rules that exist in both the previous and next rule sets as
          // rules that remained the same across this change. To get only the newly added rules,
          // we must filter rules that exist in both rule sets from the newer rule set.

          const unchangedRuleIds = previousRuleIdSet.intersection(nextRuleIdSet);
          const nextRuleIds = Array.from(nextRuleIdSet);

          const newRuleIds = nextRuleIds.filter(ruleId => !unchangedRuleIds.has(ruleId));

          // We append the action id to account for when we add a rule set to an existing action with no rule set.
          // This case does not trigger an action modified change, as the operation processors see it as a purely additive op.
          // Since the formatters eventually get stored into sets, which prunes duplicates,
          // we do not need to worry about duplicate action ids from being present within our computed mergeDiff.
          if (formattedOperation === Operation.Modify && previousRuleIdSet.size === 0) {
            return [...newRuleIds, actionPublicId];
          }

          return newRuleIds;
        });

        entriesBuffer.append(entries);
      },
    }),
    thresholdAdded: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => (descriptor === 'threshold') && (op === Operation.Add),
      ],
      process: (thresholdAddedChanges?: ThresholdAddedChange[]) => {
        const entries: MergeBufferItem[] = (thresholdAddedChanges ?? []).map(
          change => change.value.next.publicId,
        );
        entriesBuffer.append(entries);
      },
    }),
    thresholdModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => (descriptor === 'threshold') && (op === Operation.Modify),
      ],
      process: (thresholdModifiedChanges?: ThresholdModifiedChange[]) => {
        const entries: MergeBufferItem[] = (thresholdModifiedChanges ?? []).flatMap(
          // Include the threshold publicId as well for highlighting in the drawer
          change => [change.actionPublicId, change.value.next.publicId],
        );
        entriesBuffer.append(entries);
      },
    }),
    actionRemoved: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => (descriptor === 'action' && op === Operation.Remove),
      ],
      process: (removedActionChanges?: ActionRemovedChange[]) => {
        const entries: MergeBufferItem[] = (removedActionChanges ?? []).map(
          change => change.actionPublicId,
        );
        entriesBuffer.append(entries);
      },
    }),
    thresholdRemoved: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => (descriptor === 'threshold') && (op === Operation.Remove),
      ],
      process: (thresholdRemovedChanges?: ThresholdRemovedChange[]) => {
        const entries: MergeBufferItem[] = (thresholdRemovedChanges ?? []).map(
          change => change.value.prev.publicId,
        );
        entriesBuffer.append(entries);
      },
    }),
  };
}
